import {useEffect, useRef, useState} from 'react';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';
import {motion} from 'framer-motion';
import {useTranslation} from 'next-i18next';
import {useProductListView} from '@/_application/modules/products/provider/product-list-view.provider';
import {
    Aggregation,
    ProductAttributeFilterInput,
    FilterEqualTypeInput,
    FilterMatchTypeInput,
} from '__generated__/__types__';
import {filterAttributeCodeValueType} from '@/framework/static/filter-attribute-code-value-type';
import FilterByAggregation from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter/filter/filter-by-aggregation';
import FilterAggregationItem from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter/filter/components/filter-aggregation-item';
import {FilterByPriceInput} from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter-toolbar.type';
import {getImageAssetUrl} from '@/_application/core/utils/common';
import {Image} from '@/_application/core/components/ui/image';
import FilterByBrand from '@/_application/modules/categories/components/sorting-filter-toolbar/sorting-filter/filter/filter-by-brand';
import {MultiRangeSliderMethods} from './components/multi-range-slider';

interface FilterTabProps {
    attributeCodeList: string[];
    aggregationList: Aggregation[];
    onConfirm: () => void;
}

const FilterTab: React.FC<FilterTabProps> = ({aggregationList, attributeCodeList, onConfirm}) => {
    const {t} = useTranslation();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const {filter, defaultFilter} = useProductListView();
    const sliderRef = useRef<MultiRangeSliderMethods>(null);
    const [query, setQuery] = useState<Record<string, any>>({});
    const [isFilterByBrand, setIsFilterByBrand] = useState<boolean>(false);
    const [brandSearchInput, setBrandSearchInput] = useState<string>('');

    useEffect(() => {
        let defaultQuery: Record<string, any> = {};
        for (const property in filter!) {
            if (property === 'price') {
                defaultQuery.price = filter.price;
            } else if (filterAttributeCodeValueType.filterMatchType.includes(property)) {
                const filterProperty = filter[property as keyof ProductAttributeFilterInput]! as FilterMatchTypeInput;

                defaultQuery[property] = [];
                defaultQuery[property][0] = filterProperty?.match;
            } else {
                const filterProperty = filter[property as keyof ProductAttributeFilterInput]! as FilterEqualTypeInput;
                defaultQuery[property] = filterProperty?.in;
            }
        }
        setQuery(defaultQuery);
    }, []);

    const resetFilter = () => {
        if (isFilterByBrand) {
            setBrandSearchInput('');
        }
        if (sliderRef?.current?.resetValues) {
            sliderRef.current?.resetValues();
        }

        //reset others filter
        setQuery(defaultFilter ?? {});
    };

    const filterPrice = (value: FilterByPriceInput) => {
        const {min, max} = value;

        setQuery({
            ...query,
            price: {
                from: min?.toString(),
                to: max?.toString(),
            },
        });
    };

    const updateQueryUrl = () => {
        const params = new URLSearchParams(searchParams.toString());

        attributeCodeList.forEach(code => {
            if (code !== 'price' && searchParams.get(code) && !query[code]?.length) {
                params.delete(code);
            }
        });

        for (const property in query) {
            if (property === 'price') {
                const minPrice: string = query.price.from;
                const maxPrice: string = query.price.to;
                params.set('price', `${minPrice}-${maxPrice}`);
            } else if (query[property]?.length) {
                params.set(property, query[property].join(','));
            }
        }

        const qs = params.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname);
    };

    const handleConfirm = () => {
        if (isFilterByBrand) return setIsFilterByBrand(false);

        updateQueryUrl();
        onConfirm();
    };

    const handleChangeQuery = (attribute_code: string, buttonValue: string, isSelected: boolean) => {
        let selectedQuery = query[attribute_code] ?? [];
        if (filterAttributeCodeValueType.filterMatchType.includes(attribute_code) && isSelected) {
            selectedQuery[0] = buttonValue;
        } else if (filterAttributeCodeValueType.filterMatchType.includes(attribute_code)) {
            selectedQuery.shift();
        } else if (isSelected) {
            selectedQuery.push(buttonValue);
        } else {
            selectedQuery = selectedQuery.filter((item: string) => item !== buttonValue);
        }

        const newFilter = {
            ...query,
            [attribute_code]: selectedQuery,
        };

        setQuery(newFilter);
    };

    const openAggregationsFiltering = () => {
        setIsFilterByBrand(false);
    };

    const openBrandFiltering = () => {
        setIsFilterByBrand(true);
    };

    const onBrandSearchInputChange = (value: string) => {
        setBrandSearchInput(value);
    };

    const renderAggregationItem = (aggregation: Aggregation) => {
        return (
            <FilterAggregationItem
                aggregation={aggregation}
                filter={filter ?? {}}
                query={query}
                onFilterByPrice={filterPrice}
                onQueryChange={handleChangeQuery}
                openBrandFiltering={openBrandFiltering}
                ref={aggregation.attribute_code === 'price' ? sliderRef : undefined}
            />
        );
    };

    return (
        <div className="flex max-h-[75vh] w-full flex-col">
            <div className="mx-4 mb-5 flex content-center justify-between pt-4">
                <div className="flex items-center justify-center gap-5">
                    {isFilterByBrand && (
                        <motion.button
                            whileTap={{translateY: 4}}
                            className="text-sm text-gray-600"
                            onClick={openAggregationsFiltering}>
                            <Image
                                src={getImageAssetUrl('icons/gray-previous-arrow.png')}
                                alt="gray-previous-arrow-icon"
                                width={12}
                                height={12}
                            />
                        </motion.button>
                    )}

                    <p className="text-xl font-bold text-KPZ_Blue-Dark">
                        {isFilterByBrand ? t('text-brand') : t('text-filter-product')}
                    </p>
                </div>

                <motion.button whileTap={{translateY: 2}} className="text-sm text-gray-600" onClick={resetFilter}>
                    {t('text-deselect')}
                </motion.button>
            </div>

            <div className="flex flex-col justify-between overflow-y-auto overflow-x-hidden">
                {isFilterByBrand ? (
                    <FilterByBrand
                        aggregationList={aggregationList}
                        brandSearchInput={brandSearchInput}
                        query={query}
                        onQueryChange={handleChangeQuery}
                        onBrandSearchInputChange={onBrandSearchInputChange}
                    />
                ) : (
                    <FilterByAggregation
                        aggregationList={aggregationList}
                        renderAggregationItem={renderAggregationItem}
                    />
                )}
            </div>

            <div className="mb-6 flex w-full content-center justify-center pt-1">
                <motion.button
                    whileTap={{translateY: 3}}
                    transition={{type: 'spring', stiffness: 400, damping: 20}}
                    onClick={handleConfirm}
                    className="mx-4 h-10 w-full rounded-[50px] bg-KPZ_Blue-Dark px-6 py-2 text-white">
                    {!isFilterByBrand ? t('text-filter-now') : t('text-verify-code')}
                </motion.button>
            </div>
        </div>
    );
};

export default FilterTab;
